import { Alert, Button, Card, Field, PageHeader, useToast } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Building2, Plus, type LucideIcon } from 'lucide-react';
import {
  type ChangeEventHandler,
  type FocusEventHandler,
  type JSX,
  forwardRef,
  useEffect,
} from 'react';
import { type Resolver, useForm } from 'react-hook-form';

import type { CustomerFormInput } from '../../customers/api';
import {
  TAX_CONDITIONS,
  TAX_CONDITION_LABELS,
  ZONES,
  ZONE_LABELS,
  createCustomer,
} from '../../customers/api';
import { AddressFields } from '../../customers/components/address-fields';
import { FormSection } from '../../components/form-section';
import {
  DEFAULT_FORM_VALUES,
  type CustomerFormFields,
  type CustomerFormValues,
  type FieldErrorTarget,
  customerFormSchema,
  mapCreateError,
} from '../../customers/customer-form-schema';

const customerNewRoute = getRouteApi('/protected/customers/new');

/**
 * `exactOptionalPropertyTypes` treats an explicit `error={undefined}` as a
 * different (rejected) shape than the prop being absent; this omits the key
 * entirely instead of ever assigning `undefined` to it.
 */
function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}

export interface CustomerFormProps {
  defaultValues?: CustomerFormFields;
  submitLabel: string;
  /** Decorative: aria-hidden, same convention as `Button`'s own `icon` prop. */
  submitIcon?: LucideIcon;
  onSubmit: (values: CustomerFormValues) => Promise<void>;
  submitting: boolean;
  formError?: string;
  fieldError?: { field: FieldErrorTarget; message: string };
  onCancel?: () => void;
}

/**
 * `customers/spec.md` → "Pantallas de alta y detalle en back-office". Used
 * both by `/customers/new` and by `CustomerDataTab`'s edit mode
 * (`design.md` §7, orchestrator decision 4).
 */
export function CustomerForm({
  defaultValues,
  submitLabel,
  submitIcon,
  onSubmit,
  submitting,
  formError,
  fieldError,
  onCancel,
}: CustomerFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CustomerFormFields, unknown, CustomerFormValues>({
    resolver: zodResolver(customerFormSchema) as unknown as Resolver<
      CustomerFormFields,
      unknown,
      CustomerFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_FORM_VALUES,
  });

  useEffect(() => {
    if (fieldError !== undefined) {
      setError(fieldError.field, { type: 'server', message: fieldError.message });
    }
  }, [fieldError, setError]);

  return (
    <form
      onSubmit={(event) => void handleSubmit((values) => onSubmit(values))(event)}
      className="flex flex-col gap-6"
      noValidate
    >
      {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

      <FormSection title="Identificación">
        <Field
          label="Razón social"
          required
          {...register('legalName')}
          {...maybeError(errors.legalName?.message)}
        />
        <Field
          label="Nombre comercial"
          required
          {...register('tradeName')}
          {...maybeError(errors.tradeName?.message)}
        />
        <Field
          label="CUIT"
          required
          placeholder="30-12345678-1"
          {...register('taxId')}
          {...maybeError(errors.taxId?.message)}
        />
        <SelectField
          label="Condición fiscal"
          {...register('taxCondition')}
          {...maybeError(errors.taxCondition?.message)}
          options={TAX_CONDITIONS.map((value) => ({ value, label: TAX_CONDITION_LABELS[value] }))}
        />
      </FormSection>

      <FormSection title="Facturación">
        <Field
          label="Email de facturación"
          type="email"
          required
          {...register('billingEmail')}
          {...maybeError(errors.billingEmail?.message)}
        />
        <SelectField
          label="Zona"
          {...register('zone')}
          {...maybeError(errors.zone?.message)}
          options={ZONES.map((value) => ({ value, label: ZONE_LABELS[value] }))}
        />

        <div className="sm:col-span-2">
          <AddressFields
            legend="Domicilio de facturación"
            register={(field) => register(`billingAddress.${field}`)}
            errors={{
              street: errors.billingAddress?.street?.message,
              number: errors.billingAddress?.number?.message,
              city: errors.billingAddress?.city?.message,
              province: errors.billingAddress?.province?.message,
              postalCode: errors.billingAddress?.postalCode?.message,
            }}
          />
        </div>

        <div className="sm:col-span-2">
          <Field label="Notas" {...register('notes')} {...maybeError(errors.notes?.message)} />
        </div>
      </FormSection>

      <div className="flex items-center justify-end gap-3">
        {onCancel === undefined ? null : (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          {...(submitIcon === undefined ? {} : { icon: submitIcon })}
          loading={submitting}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

interface SelectFieldOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  name: string;
  error?: string | undefined;
  options: SelectFieldOption[];
  onChange: ChangeEventHandler<HTMLSelectElement>;
  onBlur: FocusEventHandler<HTMLSelectElement>;
}

/**
 * No shared `Select` component yet (`design.md` §7); mirrors
 * `CustomerSearchBar`'s pattern, plus `Field`'s error a11y wiring.
 *
 * `forwardRef` is required here, not optional style: this project's JSX
 * runtime does not forward a spread `ref` from `register()` into a plain
 * function component's props (verified directly — an un-forwarded `ref`
 * silently drops every value change, so the field always submits empty).
 */
const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, name, error, options, onChange, onBlur },
  ref,
) {
  const selectId = `customer-form-${name}`;
  const messageId = `${selectId}-message`;
  const hasError = error !== undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={selectId} className="text-sm font-medium text-slate-900">
        {label}
        <span aria-hidden="true" className="ml-0.5 text-red-600">
          *
        </span>
      </label>
      <select
        id={selectId}
        name={name}
        ref={ref}
        onChange={onChange}
        onBlur={onBlur}
        className={`h-11 rounded-md border px-3 text-sm ${hasError ? 'border-red-500' : 'border-slate-300'}`}
        aria-invalid={hasError}
        aria-describedby={hasError ? messageId : undefined}
      >
        <option value="">Seleccioná una opción</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hasError ? (
        <p id={messageId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
});

/** `customers/spec.md` → "Alta de empresa cliente", "Pantallas de alta y detalle en back-office". */
export function CustomerFormPage(): JSX.Element {
  const navigate = customerNewRoute.useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: (input: CustomerFormInput) => createCustomer(input),
    onSuccess: async (customer) => {
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast('success', 'Cliente creado');
      await navigate({
        to: '/customers/$customerId',
        params: { customerId: customer.id },
        search: { tab: 'datos' },
      });
    },
  });

  const { formError, fieldError } = mapCreateError(mutation.error);

  async function handleSubmit(values: CustomerFormValues): Promise<void> {
    // `mutation.error` (read via `mapCreateError` above) is the source of truth
    // for the UI; the rejection itself would otherwise be an unhandled promise.
    await mutation.mutateAsync(toFormInput(values)).catch(() => undefined);
  }

  return (
    <section className="flex max-w-2xl flex-col gap-6">
      <PageHeader
        icon={Building2}
        title="Nueva empresa"
        description="Carguá los datos de facturación y de contacto de la nueva empresa cliente."
      />
      <Card>
        <CustomerForm
          submitLabel="Crear empresa"
          submitIcon={Plus}
          submitting={mutation.isPending}
          onSubmit={handleSubmit}
          {...(formError === undefined ? {} : { formError })}
          {...(fieldError === undefined ? {} : { fieldError })}
        />
      </Card>
    </section>
  );
}

function toFormInput(values: CustomerFormValues): CustomerFormInput {
  const address = values.billingAddress;
  const hasAddress = Object.values(address).some((value) => value !== '');

  return {
    legalName: values.legalName,
    tradeName: values.tradeName,
    taxId: values.taxId,
    taxCondition: values.taxCondition,
    billingEmail: values.billingEmail,
    zone: values.zone,
    ...(hasAddress ? { billingAddress: address } : {}),
    ...(values.notes === '' ? {} : { notes: values.notes }),
  };
}
